<!-- A day written in full ("Tuesday 3 November", with the year when it is not this year's) that opens a calendar of one
     month: ‹ › or a sideways swipe (or a sideways trackpad scroll) change month, today has a ring, weeks start on Monday,
     the chosen day is filled and `other` (the other end of a span, e.g. the end of work for the start) is tinted, with
     the days between them lightly tinted. Keyboard: arrows move inside the calendar (Page Up / Down: a month), Enter
     picks, Esc closes. Used by WorkSlotField.vue (work start and end of work). -->
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { usePopover } from '../../composables/usePopover'
import { useI18n } from '../../composables/useI18n'
import { dayName, monthName, ucFirst } from '../../i18n'
import { addDays, addMonths, dayOf, firstOfMonth, isoDay } from '../../utils/date'
import { dayLabel, monthGrid } from '../../features/workSlot'

const props = defineProps<{ id: string; label: string; value: string; other?: string; placeholder: string }>()
const emit = defineEmits<{ pick: [day: string] }>()
const { t } = useI18n()
const today = isoDay(new Date())

const open = ref(false)
const root = ref<HTMLElement | null>(null), btn = ref<HTMLButtonElement | null>(null)
usePopover(open, root, { esc: true })
// Closed with Esc or a tap outside: the focus goes back to the button when it was inside the popover
watch(open, o => { if (!o) nextTick(() => { if (document.activeElement === document.body) btn.value?.focus() }) })

const month = ref(firstOfMonth(new Date()))
/** Direction of the last month change (the slide goes that way) */
const dir = ref(1)
/** Day with the keyboard focus inside the grid */
const focusDay = ref('')
const grid = computed(() => monthGrid(month.value))
const weekdays = Array.from({ length: 7 }, (_, i) => ucFirst(dayName(i, 'short').replace('.', '')))
const monthTitle = computed(() => `${ucFirst(monthName(month.value.getMonth()))} ${month.value.getFullYear()}`)
const monthKey = computed(() => isoDay(month.value))
const enterFrom = computed(() => (dir.value > 0 ? 'translate-x-8 opacity-0' : '-translate-x-8 opacity-0'))
const leaveTo = computed(() => (dir.value > 0 ? '-translate-x-8 opacity-0' : 'translate-x-8 opacity-0'))
const gridBox = ref<HTMLElement | null>(null)

function toggle(): void {
  if (open.value) { open.value = false; return }
  focusDay.value = props.value || props.other || today
  month.value = firstOfMonth(dayOf(focusDay.value))
  open.value = true
  focusGridDay()
}
function step(n: number): void {
  dir.value = n
  month.value = addMonths(month.value, n)
  // the keyboard focus day follows into the new month (same day number, clamped), so Tab still reaches the grid
  const b = dayOf(focusDay.value || today), m = month.value
  focusDay.value = isoDay(new Date(m.getFullYear(), m.getMonth(), Math.min(b.getDate(), new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate())))
}
let pendingFocus = false
/** Shows the month of the focused day (sliding) and puts the focus on it */
function showFocused(): void {
  const m = firstOfMonth(dayOf(focusDay.value))
  if (+m === +month.value) { focusGridDay(); return }
  dir.value = m > month.value ? 1 : -1
  month.value = m
  // the new grid appears when the old one has left (out-in): the focus goes there in afterEnter
  pendingFocus = true
}
function goToday(): void { focusDay.value = today; showFocused() }
function focusGridDay(): void {
  nextTick(() => gridBox.value?.querySelector<HTMLButtonElement>(`[data-day="${focusDay.value}"]`)?.focus({ preventScroll: true }))
}
function afterEnter(): void { if (pendingFocus) { pendingFocus = false; focusGridDay() } }
let swipedAt = 0
function pick(iso: string): void {
  if (Date.now() - swipedAt < 400) return
  emit('pick', iso)
  open.value = false
  btn.value?.focus()
}
function gridKey(e: KeyboardEvent): void {
  const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }
  const base = dayOf(focusDay.value || today)
  let next: Date | null = null
  if (e.key in moves) next = addDays(base, moves[e.key])
  else if (e.key === 'PageUp' || e.key === 'PageDown') {
    const n = e.key === 'PageUp' ? -1 : 1, m = addMonths(base, n)
    next = new Date(m.getFullYear(), m.getMonth(), Math.min(base.getDate(), new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate()))
  }
  if (!next) return
  e.preventDefault()
  focusDay.value = isoDay(next)
  showFocused()
}
// Sideways swipe (touch, pen or mouse drag) and sideways trackpad scroll change month
let swipe: { x: number; y: number } | null = null
function swipeStart(e: PointerEvent): void { swipe = { x: e.clientX, y: e.clientY } }
function swipeEnd(e: PointerEvent): void {
  if (!swipe) return
  const dx = e.clientX - swipe.x, dy = e.clientY - swipe.y
  swipe = null
  if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) { swipedAt = Date.now(); step(dx < 0 ? 1 : -1) }
}
let wheelAt = 0
function wheel(e: WheelEvent): void {
  if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || Math.abs(e.deltaX) < 15) return
  e.preventDefault()
  if (Date.now() - wheelAt < 450) return
  wheelAt = Date.now()
  step(e.deltaX > 0 ? 1 : -1)
}
/** The days between the chosen day and the other one (the span of the work) */
const between = (iso: string): boolean => {
  const a = props.value, b = props.other
  if (!a || !b) return false
  return a < b ? iso > a && iso < b : iso > b && iso < a
}
const cellCls = (d: { iso: string; inMonth: boolean }) => ['h-9 rounded-full text-xs font-bold tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
  d.iso === props.value ? 'bg-brand text-white' : d.iso === props.other ? 'bg-brand/30 text-gray-100 hover:bg-brand/40'
    : d.iso === today ? 'ring-1 ring-brand text-brand hover:bg-brand/10' : between(d.iso) ? 'bg-brand/10 text-gray-100 hover:bg-brand/20'
      : d.inMonth ? 'text-gray-200 hover:bg-brand/10' : 'text-gray-500 hover:bg-brand/10']
</script>

<template>
  <div ref="root" class="relative min-w-0 flex-1">
    <button :id="props.id" ref="btn" type="button" :aria-expanded="open" aria-haspopup="dialog" :title="props.label" :aria-label="`${props.label}: ${dayLabel(props.value) || props.placeholder}`"
      class="w-full min-h-12 py-2 flex items-center gap-2.5 bg-darkinput border border-gray-800 rounded-lg px-3 text-left transition hover:border-brand/60 focus:outline-none focus:border-brand" @click="toggle">
      <i class="fas fa-calendar-day text-brand flex-shrink-0"></i>
      <span :class="['leading-tight', props.value ? 'font-bold text-gray-100' : 'text-gray-500']">{{ dayLabel(props.value) || props.placeholder }}</span>
    </button>
    <div v-if="open" :id="`${props.id}Picker`" role="dialog" :aria-label="props.label" class="absolute z-30 top-full mt-2 left-0 w-[min(19rem,calc(100vw-3rem))] p-3 space-y-2 bg-darkcard border border-gray-800 rounded-2xl shadow-2xl">
      <div class="flex items-center gap-2">
        <button type="button" :title="t('Previous month')" :aria-label="t('Previous month')" class="w-8 h-8 rounded-full bg-darkbtn text-gray-400 hover:text-brand" @click="step(-1)"><i class="fas fa-chevron-left text-xs"></i></button>
        <div class="flex-1 text-center font-bold text-gray-100" aria-live="polite">{{ monthTitle }}</div>
        <button type="button" :title="t('Next month')" :aria-label="t('Next month')" class="w-8 h-8 rounded-full bg-darkbtn text-gray-400 hover:text-brand" @click="step(1)"><i class="fas fa-chevron-right text-xs"></i></button>
      </div>
      <div class="grid grid-cols-7 text-center text-[10px] font-bold text-gray-500 uppercase"><span v-for="w in weekdays" :key="w">{{ w }}</span></div>
      <div ref="gridBox" class="overflow-hidden touch-pan-y select-none" @pointerdown="swipeStart" @pointerup="swipeEnd" @pointercancel="swipe = null" @wheel="wheel" @keydown="gridKey">
        <Transition mode="out-in" :enter-from-class="enterFrom" enter-active-class="transition duration-150 ease-out" leave-active-class="transition duration-100 ease-in" :leave-to-class="leaveTo" @after-enter="afterEnter">
          <div :key="monthKey" class="grid grid-cols-7 gap-0.5">
            <button v-for="d in grid" :key="d.iso" type="button" :data-day="d.iso" :tabindex="d.iso === focusDay ? 0 : -1" :aria-current="d.iso === today ? 'date' : undefined"
              :aria-pressed="d.iso === props.value" :aria-label="dayLabel(d.iso)" :class="cellCls(d)" @click="pick(d.iso)" @focus="focusDay = d.iso">{{ d.day }}</button>
          </div>
        </Transition>
      </div>
      <div class="flex justify-center pt-1"><button type="button" class="px-3 py-1.5 rounded-full text-xs font-bold text-brand hover:bg-brand/10" @click="goToday">{{ t('Today') }}</button></div>
    </div>
  </div>
</template>
